import { useState } from 'react';
import { Phone, Mail, MapPin, MessageCircle, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast.success('Thank you! Your inquiry has been submitted.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+94 74 365 2899',
      href: 'tel:+94743652899'
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'info@aspireacademy.lk',
      href: 'mailto:info@aspireacademy.lk'
    },
    {
      icon: MapPin,
      label: 'Address',
      value: '123 Academic Street, Colombo 07, Sri Lanka',
      href: null
    },
    {
      icon: Clock,
      label: 'Office Hours',
      value: 'Mon - Sat: 9:00 AM - 6:00 PM',
      href: null
    }
  ];

  return (
    <main data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with Aspire Academy. Located at 102/5A Dharmapala Mawatha, Colombo 7. Call +94 74 365 2899 or WhatsApp us for inquiries about IGCSE and A Level tuition."
        path="/contact"
      />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }, { name: 'Contact Us' }]} />
      {/* Hero */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-uppercase text-aspire-red">Get in Touch</span>
            <h1 className="heading-hero mt-2 mb-6">
              Contact <span className="text-aspire-red">Us</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Have questions about our programs? Ready to enroll? Get in touch with our team 
              and we'll help you get started on your academic journey.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-heading font-bold text-2xl mb-8">Contact Information</h2>
              
              <div className="space-y-6 mb-8">
                {contactInfo.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-aspire-red/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-aspire-red" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="font-medium text-charcoal hover:text-aspire-red transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-medium text-charcoal">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* WhatsApp CTA */}
              <a 
                href="https://wa.me/94743652899"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white py-4 font-bold uppercase tracking-wider transition-colors"
                data-testid="whatsapp-contact-btn"
              >
                <MessageCircle className="w-6 h-6" />
                Chat on WhatsApp
              </a>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-surface p-4 sm:p-8 border border-gray-200">
                <h2 className="font-heading font-bold text-2xl mb-6">Send Us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="mt-1"
                        placeholder="Enter your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="mt-1"
                        placeholder="Enter your email"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 sm:gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="mt-1"
                        placeholder="Enter your phone"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="mt-1"
                        placeholder="e.g., IGCSE Mathematics Inquiry"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="mt-1"
                      placeholder="Tell us about your inquiry..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button 
                    type="submit"
                    disabled={submitting}
                    className="btn-primary flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-surface">
        <div className="container-main py-8">
          <h2 className="font-heading font-bold text-xl mb-4">Our Location</h2>
        </div>
        <div className="h-[250px] sm:h-[400px] bg-gray-200" data-testid="map-container">
          <iframe 
            src="https://maps.google.com/maps?q=Aspire+Academy,+102/5A+Dharmapala+Mawatha,+Colombo+07,+Sri+Lanka&t=&z=17&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Aspire Academy Location"
          />
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
